import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Loader, Modal } from "@monorepo/components";
import { API_DOMAIN } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { errorTitle, isResponseErrorArray } from "@monorepo/helpers";
import { PdfUploadIcon } from "@monorepo/icons";
import { useLazyGetUpdateServiceInformationQuery } from "@monorepo/services/api/services";
import { setService } from "@monorepo/slices/service";
import theme, { iconsSizes } from "@monorepo/styles/theme";
import { FormStepsEnum, IServiceParameter } from "@monorepo/types";
import Box from "@mui/material/Box/Box";
import Button from "@mui/material/Button";
import { reverse } from "named-urls";

import { useAppDispatch, useAppSelector } from "hooks";

const PdfUpload = () => {
  const auth = useAppSelector(({ authSlice }) => authSlice);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [updateServiceInformation] = useLazyGetUpdateServiceInformationQuery();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent) => {
    setIsLoading(true);
    const target = e.target as HTMLInputElement;
    if (!target.files) {
      return;
    }
    handleSubmit(target.files[0]);
  };

  const handleSubmit = (file: File) => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    if (!formData.get("file")) {
      return;
    }

    fetch(`${API_DOMAIN}/api/v1/services/scan-pdf-document`, {
      headers: {
        authorization: `${auth.token_type} ${auth.access_token}`,
        accept: "application/vnd.api+json",
        platform: "web",
      },
      method: "POST",
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((res) => {
        if (res.data) {
          const data = res.data;
          const attributes = data.attributes;
          const parameters = attributes.parameters as Array<IServiceParameter>;
          const firstStep = parameters.find((param) => param.step === FormStepsEnum.ACCOUNT_VERIFICATION);
          const firstStepCompleted = !!firstStep?.value;
          if (!firstStepCompleted) {
            toast.success(t("common.pdf_uploaded_successfully"));
            dispatch(setService({ ...attributes, id: data.id }));
            history.push(
              reverse(routeConfig.dashboard.payments.addService.serviceId, {
                serviceId: data.id,
              })
            );
          } else {
            updateServiceInformation({
              service_id: data.id,
              service_parameters: [firstStep.parameter_name],
              values: [firstStep.value],
            })
              .then((updateRes) => {
                if (!updateRes.data) {
                  return;
                }
                toast.success(t("common.pdf_uploaded_successfully"));
                dispatch(setService({ ...attributes, id: data.id }));
                history.push(
                  reverse(routeConfig.dashboard.payments.addService.serviceId, {
                    serviceId: data.id,
                  })
                );
              })
              .catch((error) => {
                if (isResponseErrorArray(error)) {
                  toast.error(errorTitle(error));
                }
              });
          }
        } else if (res.errors) {
          if (isResponseErrorArray(res.errors)) {
            toast.error(errorTitle(res.errors));
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleButtonClick = () => {
    if (!inputRef || !inputRef.current) return;
    inputRef.current.click();
  };

  return (
    <Box mb={2}>
      <Button
        variant="contained"
        type="button"
        disableElevation
        sx={{ marginBottom: theme.spacing(2), width: "100%" }}
        startIcon={<PdfUploadIcon sx={{ width: 30, height: 30, fontSize: iconsSizes.small }} />}
        onClick={handleButtonClick}
      >
        {t("payments.upload_pdf")}
      </Button>
      <input
        ref={inputRef}
        style={{ display: "none" }}
        type="file"
        name="pdf"
        accept="application/pdf"
        onChange={handleFileChange}
        onClick={(event) => {
          // allow uploading the same file
          // eslint-disable-next-line no-param-reassign
          (event.target as HTMLInputElement).value = "";
        }}
      />
      {isLoading && (
        <Modal open={isLoading} unclosable onClose={() => {}}>
          {t("common.pdf_being_uploaded")}
          <Loader />
        </Modal>
      )}
    </Box>
  );
};

export default PdfUpload;
